import { Button, Card, Text } from '@components/ui';
import AddressDetails from '@features/buyer/hire/address-details/address-details';
import ContactDetails from '@features/buyer/hire/contact-details/contact-details';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import JobDetails from '@features/buyer/hire/job-details/job-details';
import JobPriceCalculator from '@features/buyer/hire/job-price-calculator/job-price-calculator';
import PaymentStep from '@features/buyer/hire/payment-step/payment-step';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { HireCancelDialog } from './hire-cancel-dialog';
import { HireStepper } from './hire-stepper';
import { HireSummary } from './hire-summary';
import type { HireStep, HireStepKey } from './hireR8trTypes';

const FIRST_STEP: HireStepKey = 'JOB_PRICE_CALCULATOR';


const buildSteps = (isAutomotive: boolean): HireStep[] => [
  { key: 'JOB_PRICE_CALCULATOR', copyKey: 'pricing' },
  { key: 'JOB_DETAILS', copyKey: isAutomotive ? 'vehicle' : 'item' },
  { key: 'ADDRESS_DETAILS_KEY', copyKey: 'location' },
  { key: 'CONTACT_DETAILS_KEY', copyKey: 'contact' },
  { key: 'PAYMENT', copyKey: 'payment' },
];

const HireR8tr = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createJob, resetJob } = useHireJob();
  const [activeKey, setActiveKey] = useState<HireStepKey>(FIRST_STEP);
  const [completed, setCompleted] = useState<ReadonlySet<HireStepKey>>(
    () => new Set()
  );
  const [confirmCancel, setConfirmCancel] = useState(false);

  const isAutomotive = createJob?.isAutomotive !== false;
  const steps = useMemo(() => buildSteps(isAutomotive), [isAutomotive]);
  const activeIndex = steps.findIndex((step) => step.key === activeKey);
  const activeStep = steps[activeIndex];

  useEffect(() => {
    resetJob();
    return () => {
      resetJob();
    };
  }, [resetJob]);

  const goTo = (key: HireStepKey): void => {
    setActiveKey(key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onNext = (): void => {
    const next = steps[activeIndex + 1];
    if (!next) {
      return;
    }
    setCompleted((prev) => new Set(prev).add(activeKey));
    goTo(next.key);
  };

  const previous = steps[activeIndex - 1];
  const navigation = {
    onBack: previous ? () => goTo(previous.key) : undefined,
    onCancel: () => setConfirmCancel(true),
  };

  const handleTosterFire = (message: string): void => {
    toast.error(message);
  };

  const leave = (): void => {
    setConfirmCancel(false);
    resetJob();
    navigate('/');
  };

  const renderStep = (key: HireStepKey): JSX.Element => {
    switch (key) {
      case 'JOB_PRICE_CALCULATOR':
        return (
          <JobPriceCalculator
            {...navigation}
            onPressNext={onNext}
            handleTosterFire={handleTosterFire}
          />
        );
      case 'JOB_DETAILS':
        return (
          <JobDetails
            {...navigation}
            onPressNext={onNext}
            handleTosterFire={handleTosterFire}
          />
        );
      case 'ADDRESS_DETAILS_KEY':
        return (
          <AddressDetails
            {...navigation}
            onPressNext={onNext}
            handleTosterFire={handleTosterFire}
          />
        );
      case 'CONTACT_DETAILS_KEY':
        return <ContactDetails {...navigation} onPressNext={onNext} />;
      case 'PAYMENT':
        return (
          <PaymentStep {...navigation} handleTosterFire={handleTosterFire} />
        );
      default: {
        const unreachable: never = key;
        return unreachable;
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1120,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 3,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <HireStepper
          steps={steps}
          activeKey={activeKey}
          completed={completed}
          onSelect={goTo}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' },
        }}
      >
        <Card
          component="section"
          aria-labelledby="hire-step-title"
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: { md: 520 },
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 28,
              }}
            >
              <Text variant="caption" tone="muted" sx={{ fontWeight: 600 }}>
                {t('hire.flow.stepOf', {
                  current: activeIndex + 1,
                  total: steps.length,
                })}
              </Text>
              {previous ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={navigation.onCancel}
                  sx={{ mr: -1 }}
                >
                  {t('hire.flow.cancel')}
                </Button>
              ) : null}
            </Box>
            <Text
              id="hire-step-title"
              variant="title"
              component="h2"
              sx={{ mt: 0.5, fontWeight: 700 }}
            >
              {t(`hire.flow.steps.${activeStep.copyKey}.title`)}
            </Text>
            <Text variant="body" tone="muted" sx={{ mt: 0.75, fontSize: 14 }}>
              {t(`hire.flow.steps.${activeStep.copyKey}.description`)}
            </Text>
          </Box>
          <Box
            key={activeKey}
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              animation: 'hire-step-in 0.24s ease-out',
              '@keyframes hire-step-in': {
                from: { opacity: 0, transform: 'translateY(6px)' },
                to: { opacity: 1, transform: 'none' },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {renderStep(activeKey)}
          </Box>
        </Card>

        <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
          <HireSummary />
        </Box>
      </Box>

      <HireCancelDialog
        open={confirmCancel}
        onStay={() => setConfirmCancel(false)}
        onLeave={leave}
      />
    </Box>
  );
};

export default HireR8tr;
